import type { AxiosError } from 'axios';

import { URL } from 'url';

/**
 * Check if a value is an Axios cancel exception. Even though digging into the
 * implementation details of Axios like this is a bit fragile, it is better than
 * always bundling the whole Axios library anywhere where we use these exception
 * utilities.
 */
function isAxiosCancel(value: unknown): boolean {
  return Boolean(value && (value as any).__CANCEL__);
}

function getTypeName(obj: unknown): string {
  if (typeof obj === 'undefined') {
    return 'undefined';
  }

  if (obj == null) {
    return 'null';
  }

  const proto = Object.getPrototypeOf(obj);
  const typeName = proto.name ?? proto.constructor?.name ?? typeof obj;

  if (typeof (obj as any).type === 'string') {
    return typeName + ':' + (obj as any).type;
  }

  return typeName;
}

/*
  We want to be able to provide more context to an error without throwing away
  the original error details. Therefore we wish to include the original error as
  the "cause" of any new error we construct, so that we have a chain of error
  causality.
*/

export interface ErrorDetails {
  messages: string[],
  debug: string[],
  syscall?: number,
  address?: string,
  port?: number,
  url?: string,
  method?: string,
  analysis?: string[],
  code?: string,
  data?: any;
  stack?: string;
  [key: string]: unknown;
}

function stringOrUndefined(value: unknown) {
  return typeof value === 'string' ? value : undefined;
}

function addIfStringAndMissing(set: Set<string>, value: unknown) {
  if (typeof value !== 'string' || !value) {
    return;
  }

  set.add(value);
}

class DetailsBuilder {
  messages = new Set<string>();
  debug = new Set<string>();

  addMessageIfStringAndMissing(value: unknown) {
    addIfStringAndMissing(this.messages, value);
  }

  addDebugIfStringAndMissing(value: unknown) {
    addIfStringAndMissing(this.debug, value);
  }
}

function asAnyError(err: unknown): Record<string, any> {
  if (err != null && (typeof  err) === 'object') {
    return err as Record<string, any>;
  }

  return { message: '' + err };
}

export function getErrorDetails(error: unknown): ErrorDetails {
  const err: Record<string, any> = asAnyError(error);
  const builder = new DetailsBuilder();
  const cause = typeof err.cause === 'function' ? null : err.cause;

  const inner: ErrorDetails = cause ? getErrorDetails(cause) : { messages: [], debug: [] };
  builder.addMessageIfStringAndMissing(err.response?.data?.error?.message);
  builder.addMessageIfStringAndMissing(err.response?.data?.error);
  builder.addMessageIfStringAndMissing(err.response?.data?.message);
  builder.addMessageIfStringAndMissing(err.response?.statusText);
  builder.addMessageIfStringAndMissing(err.statusText);
  builder.addMessageIfStringAndMissing(err.message);
  builder.addMessageIfStringAndMissing(err.stderr);
  builder.addMessageIfStringAndMissing(err.stdout);
  const axiosErrorProbe: Partial<AxiosError> = err;
  let url = err.config?.url;
  if (url != null && typeof url === 'string') {
    /*
      Redact query parameters, since they may contain secrets we don't want
      logged, and generally may clutter the error reporting.
    */
    const pos = url.indexOf('?');
    if (pos != -1 && pos + 4 < url.length) {
      url = url.substr(0, pos + 1) + '...';
    }
  }

  const respError = err.response?.data?.error ?? {};
  const respPayload = respError?.payload ?? {};
  const respInfo = {
    ...respPayload,
    developerMessage: undefined,
    messages: undefined
  };

  builder.addMessageIfStringAndMissing(respError.developerMessage);

  if (Array.isArray(respPayload.messages)) {
    for (const msg of respPayload.messages) {
      builder.addMessageIfStringAndMissing(msg);
    }
  }

  if (isAxiosCancel(err)) {
    builder.addMessageIfStringAndMissing('Cancel');
  }
  for (const msg of inner.messages) {
    builder.addMessageIfStringAndMissing(msg);
  }

  builder.addDebugIfStringAndMissing(err.name ?? getTypeName(error));

  for (const msg of inner.debug) {
    builder.addDebugIfStringAndMissing(msg);
  }

  if (err.message == 'Request aborted' && err.code == 'ECONNABORTED' && err.config?.url) {
    let u: URL | undefined;
    try {
      u = new URL(err.config.url);
    } catch {
      // Ignore
    }
    if (u) {
      const proto = u.protocol.slice(0, -1).toUpperCase();
      builder.addMessageIfStringAndMissing(proto + ' request network error');
      if (proto == 'HTTP' || proto == 'HTTPS') {
        builder.addMessageIfStringAndMissing('Could be caused by missing CORS headers in response');
        builder.addMessageIfStringAndMissing('Could be caused by untrusted HTTPS certificate');
      }
    }
  }

  let stack: string | undefined = err.stack;
  if (inner.stack) {
    stack = (stack ? (stack + '\n') : '') + inner.stack;
  }

  let messages = [...builder.messages];
  let debug = [...builder.debug];
  if (messages.length == 0) {
    messages = debug;
    debug = [];
  }

  const potential = {
    ...respInfo,
    syscall: err.syscall,
    address: err.address,
    port: err.port,
    url: url,
    method: err.config?.method,
    code: respError.statusCode ?? err.code ?? err.response?.status ?? err.status ?? err.statusCode,
    requestId: err.response?.headers?.['x-amzn-requestid'],
    analysis: err.analysis,
    data: stringOrUndefined(axiosErrorProbe?.response?.data),
    messages,
    debug,
    stack
  };

  const result = {
    ...inner,
    ...err['$metadata'],
    ...err.info
  };

  for (const kv of Object.entries(potential)) {
    if (kv[1] == null) {
      continue;
    }
    result[kv[0]] = kv[1];
  }

  return result;
}

/**
 * Try to create a text summary of an exception value, which may or may not be
 * of an Error type.
 */
export function exceptionToString(ex: unknown, showStackTrace = true): string {
  let result = exceptionAnalysis(ex);
  while (ex) {
    if (typeof ex !== 'object') {
      break;
    }
    const err = ex as { cause: unknown, stack: unknown };
    if (typeof err.stack === 'string' && showStackTrace) {
      result += '\n' + err.stack;
    }

    ex = err.cause;
  }
  return result;
}

export function exceptionEssence(ex: unknown): string {
  const details = getErrorDetails(ex);
  return details.messages[0] ?? JSON.stringify(details);
}

export function exceptionSummary(ex: unknown): string {
  const details = getErrorDetails(ex);
  return details.messages.join(', ');
}

export function allExceptionDetails(ex: unknown) {
  return getErrorDetails(ex);
}

export function exceptionAnalysis(ex: unknown): string {
  const details = getErrorDetails(ex);
  let result = details.messages.join(', ');
  const obj = { ...details, messages: undefined, stack: undefined };
  if (Object.keys(obj).length > 0) {
    result += ' ' + JSON.stringify(obj);
  }
  return result;
}

/**
 * While generally you would like to keep the original exception, certain
 * exceptions such as the Axios error responses contain native resources such as
 * socket objects. Keeping these around, and propagating them up to arbitrary
 * exception handles have proven to cause hard to debug error messages, such as
 * "TypeError: Cannot read property '_hadError' of undefined" in
 * _http_client.js, and circular reference errors when trying to format the
 * exception.
 *
 * In such cases makeCollapsedError() can be used to strip away all the exotic
 * stuff from errors while keeping all of the details we may want for
 * troubleshooting.
 */

export function makeCollapsedError(name: string, message: string, options?: { cause?: unknown, info?: Record<string, unknown> }) {
  const error = new Error(message);
  error.name = name;

  const extras: Record<string, unknown> & { info?: Record<string, unknown> } = {};
  if (options?.cause) {
    Object.assign(extras, allExceptionDetails(options.cause));
  }

  (error as any).info = { ...extras, ...options?.info };
  return error;
}

export const makeError = (message: string, name: string, options?: { info?: Record<string, unknown>, cause?: any }): Error => {
  const err = new Error(message);
  err.name = name;
  Object.assign(err, options);
  return err;
};

export function exceptionName(ex: unknown): string | undefined {
  const name = (ex as any)?.name;
  if (typeof name === 'string') {
    return name;
  }

  return undefined;
}

export function reconstitutedException(ex: unknown) {
  let info: Record<string, unknown>;
  if (typeof ex !== 'object') {
    info = { message: '' + ex };
  } else {
    info = { ...ex };
  }

  let result: Error & { info?: Record<string, unknown> };
  if (typeof info.message === 'string') {
    result = new Error(info.message);
    delete info.message;
  } else {
    result = new Error();
  }

  if (typeof info.name === 'string') {
    result.name = info.name;
    delete info.name;
  }

  result.info = info;
  return result;
}
